import { Box, Stack, Typography, Chip, Button } from "@mui/material";
import { NavLink } from "react-router-dom";
import { helper } from "../../../../utils/helper.js";
import Constants from "../../../../utils/constants.js";
import {EngineOnIcon} from "../../../../utils/ui/Icons/EngineOnIcon.js";
import {EngineOffIcon} from "../../../../utils/ui/Icons/EngineOff.js";

const START_MIN = 7 * 60;
const TOTAL_MIN = 13 * 60;
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);

function parseTimeStr(str) {
    if (!str) return null;
    // Handle "HHhMM" format
    const hMatch = str.match(/^(\d{1,2})h(\d{2})$/);
    if (hMatch) return parseInt(hMatch[1]) * 60 + parseInt(hMatch[2]);
    // Handle datetime string
    try {
        const d = new Date(str);
        if (!isNaN(d)) return d.getHours() * 60 + d.getMinutes();
    } catch (_) {}
    return null;
}

function toPct(min) {
    if (min === null || min === undefined) return null;
    return Math.max(0, Math.min(100, ((min - START_MIN) / TOTAL_MIN) * 100));
}

function formatMinutes(min) {
    if (min === null || min === undefined) return null;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h${String(m).padStart(2, "0")}`;
}

function getTimeMin(val) {
    if (!val) return null;
    // "HHhMM" format — already local time, no timezone issue
    if (typeof val === "string") {
        const hMatch = val.match(/^(\d{1,2})h(\d{2})$/);
        if (hMatch) return parseInt(hMatch[1]) * 60 + parseInt(hMatch[2]);
    }
    // Datetime string — use helper (moment.parseZone) to respect the embedded timezone offset,
    // same logic as getDisplayTime/getFormattedTime so position matches the displayed label
    try {
        const formatted = helper.getFormattedTime(val);
        if (formatted && formatted !== "-") {
            const m = formatted.match(/^(\d{1,2})h(\d{2})$/);
            if (m) return parseInt(m[1]) * 60 + parseInt(m[2]);
        }
    } catch (_) {}
    // Last resort fallback (no timezone correction)
    try {
        const d = new Date(val);
        if (!isNaN(d)) return d.getHours() * 60 + d.getMinutes();
    } catch (_) {}
    return null;
}

function getDisplayTime(val) {
    if (!val) return null;
    // Try helper first
    try {
        const formatted = helper.getFormattedTime(val);
        if (formatted && formatted !== "-") return formatted;
    } catch (_) {}
    const min = getTimeMin(val);
    if (min !== null) return formatMinutes(min);
    return null;
}

export default function EquipageVehicleTimeline({ data }) {
    const engineOnRaw = data?.laveusePrisePoste?.engineOn || null;
    const engineOffRaw = data?.laveuseFinPoste?.engineOff || null;

    const engineOnMin = getTimeMin(engineOnRaw);
    const engineOffMin = getTimeMin(engineOffRaw);
    const engineOnPct = toPct(engineOnMin);
    const engineOffPct = toPct(engineOffMin);
    const engineOnLabel = engineOnRaw ?? null;
    const engineOffLabel = engineOffRaw ?? null;
    // Build idle segments from reporting.idle_times (computed by cron from speed=0 periods)
    const idleTimesRaw = data?.reporting?.idle_times || [];
    const idleSegments = idleTimesRaw
        .map((seg) => ({
            startMin: getTimeMin(seg.start),
            endMin: getTimeMin(seg.end),
            durationSeconds: seg.duration_seconds ?? null,
        }))
        .filter((seg) => seg.startMin !== null && seg.endMin !== null);

    // Operator events
    const posteStartMin = getTimeMin(data?.feuilleDeTemps?.heureDebut);
    const posteEndMin = getTimeMin(data?.feuilleDeTemps?.heureFin);
    const firstWashMin = getTimeMin(data?.feuilleDeTemps?.premiereIntervention);
    const lastWashMin = getTimeMin(data?.feuilleDeTemps?.derniereIntervention);

    const opEvents = [
        posteStartMin !== null && {
            min: posteStartMin,
            label: "Prise de poste",
            color: "#374151",
            hollow: true,
            display: getDisplayTime(data?.feuilleDeTemps?.heureDebut),
        },
        firstWashMin !== null && {
            min: firstWashMin,
            label: "1er lavage",
            color: "#22c55e",
            hollow: false,
            display: getDisplayTime(data?.feuilleDeTemps?.premiereIntervention),
        },
        lastWashMin !== null && {
            min: lastWashMin,
            label: "Dernier lavage",
            color: "#22c55e",
            hollow: false,
            display: getDisplayTime(data?.feuilleDeTemps?.derniereIntervention),
        },
        posteEndMin !== null && {
            min: posteEndMin,
            label: "Fin de poste",
            color: "#374151",
            hollow: true,
            display: getDisplayTime(data?.feuilleDeTemps?.heureFin),
        },
    ].filter(Boolean);

    const TRACK_H = 14;

    // Footer metrics
    const reporting = data?.reporting;
    const engineMin = (engineOnMin !== null && engineOffMin !== null) ? engineOffMin - engineOnMin : null;
    const totalIdleMin = idleSegments.reduce((a, s) => a + (s.endMin - s.startMin), 0);

    return (
        <Box sx={{ background: "#f8fafc", borderRadius: "10px", border: "1px solid #e5e7eb", p: "18px 20px" }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" sx={{ fontSize: "12px", color: "#374151", letterSpacing: "0.05em" }}>
                    🚛 Chronologie véhicule{data?.laveusePrisePoste?.description ? ` — ${data.laveusePrisePoste.description}` : ""}{data?.laveusePrisePoste?.immatriculation ? ` ${data.laveusePrisePoste.immatriculation}` : ""}
                </Typography>
                <Box display="flex" gap={1}>
                    {data?.laveusePrisePoste?.id && data?.date && (
                        <Button
                            component={NavLink}
                            to={Constants.APP_ROUTE_LAVEUSES_TRIPS_PAGE.replace(':id', data.laveusePrisePoste.id) + `?date=${data.date}`}
                            variant="outlined"
                            size="small"
                            color="primary"
                        >
                            Trajet {data?.laveusePrisePoste?.description} - {data?.laveusePrisePoste?.immatriculation}
                        </Button>
                    )}
                    {data?.laveuseFinPoste?.id && (data?.laveusePrisePoste?.id !== data?.laveuseFinPoste?.id) && data?.date && (
                        <Button
                            component={NavLink}
                            to={Constants.APP_ROUTE_LAVEUSES_TRIPS_PAGE.replace(':id', data.laveuseFinPoste.id) + `?date=${data.date}`}
                            variant="outlined"
                            size="small"
                            color="primary"
                        >
                            Trajet {data?.laveuseFinPoste?.description} - {data?.laveuseFinPoste?.immatriculation}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Timeline grid */}
            <Box sx={{ position: "relative", mx: "2px" }}>

                {/* Hour axis */}
                <Box sx={{ position: "relative", height: "28px", mb: "6px" }}>
                    <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, height: "1px", background: "#cbd5e1" }} />
                    {HOURS.map((h) => {
                        const pct = ((h * 60 - START_MIN) / TOTAL_MIN) * 100;
                        if (pct < 0 || pct > 100) return null;
                        const isEven = h % 2 === 0;
                        return (
                            <Box key={h} sx={{ position: "absolute", left: `${pct}%`, top: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Box sx={{ width: "1px", height: isEven ? "14px" : "8px", background: isEven ? "#94a3b8" : "#cbd5e1" }} />
                                {isEven && (
                                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#64748b", mt: "1px", transform: "translateX(-50%)", position: "absolute", top: "16px", whiteSpace: "nowrap" }}>
                                        {h}h00
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* Row 1 — Laveuse */}
                <Box sx={{ mb: "4px" }}>
                    <Typography sx={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: "20px" }}>
                        Laveuse
                    </Typography>
                    <Box sx={{ position: "relative", height: `${TRACK_H + 32}px` }}>
                        {/* base track */}
                        <Box sx={{ position: "absolute", left: 0, right: 0, top: "20px", height: `${TRACK_H}px`, background: "#e2e8f0", borderRadius: "4px" }} />
                        {/* engine ON segment */}
                        {engineOnPct !== null && engineOffPct !== null && (
                            <Box sx={{ position: "absolute", left: `${engineOnPct}%`, width: `${engineOffPct - engineOnPct}%`, top: "20px", height: `${TRACK_H}px`, background: "#bbf7d0", border: "1px solid #86efac", borderRadius: "4px" }} />
                        )}
                        {/* idle segments */}
                        {idleSegments.map((seg, i) => {
                            const sp = toPct(seg.startMin);
                            const ep = toPct(seg.endMin);
                            const startLabel = formatMinutes(seg.startMin);
                            const endLabel = formatMinutes(seg.endMin);
                            const durationMin = seg.durationSeconds != null ? Math.round(seg.durationSeconds / 60) : (seg.endMin - seg.startMin);
                            const tooltipText = `Idle : ${startLabel} → ${endLabel} (${durationMin} min)`;
                            return (
                                <Box key={i} title={tooltipText} sx={{ position: "absolute", left: `${sp}%`, width: `${ep - sp}%`, top: "20px", height: `${TRACK_H}px`, background: "#fef08a", border: "1px solid #fde047", borderRadius: "3px", cursor: "help", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#854d0e", whiteSpace: "nowrap" }}>idle</Typography>
                                </Box>
                            );
                        })}
                        {/* engine ON dot */}
                        {engineOnPct !== null && (
                            <Box sx={{ position: "absolute", left: `${engineOnPct}%`, top: "14px", transform: "translateX(-50%)" }}>
                                <Box sx={{ width: "12px", height: "12px", borderRadius: "50%", background: "#22c55e", border: "2px solid white", boxShadow: "0 0 0 1.5px #22c55e", mx: "auto" }} />
                                <Typography sx={{ position: "absolute", bottom: "14px", left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", fontSize: "11px", fontWeight: 700, color: "#22c55e" }}>
                                    {engineOnLabel}
                                </Typography>
                            </Box>
                        )}
                        {/* engine OFF dot */}
                        {engineOffPct !== null && (
                            <Box sx={{ position: "absolute", left: `${engineOffPct}%`, top: "14px", transform: "translateX(-50%)" }}>
                                <Box sx={{ width: "12px", height: "12px", borderRadius: "50%", background: "#64748b", border: "2px solid white", boxShadow: "0 0 0 1.5px #94a3b8", mx: "auto" }} />
                                <Typography sx={{ position: "absolute", bottom: "14px", left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", fontSize: "11px", fontWeight: 700, color: "#64748b" }}>
                                    {engineOffLabel}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Row 2 — Opérateur */}
                <Box sx={{ mt: "12px" }}>
                    <Typography sx={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: "28px" }}>
                        Opérateur
                    </Typography>
                    <Box sx={{ position: "relative", height: `${TRACK_H + 44}px` }}>
                        {/* thin baseline */}
                        <Box sx={{ position: "absolute", left: 0, right: 0, top: "24px", height: "1px", background: "#e2e8f0" }} />
                        {/* connector line */}
                        {opEvents.length > 1 && (
                            <Box sx={{
                                position: "absolute",
                                left: `${toPct(opEvents[0].min)}%`,
                                width: `${toPct(opEvents[opEvents.length - 1].min) - toPct(opEvents[0].min)}%`,
                                top: "24px", height: "2px", background: "#e2e8f0",
                            }} />
                        )}
                        {opEvents.map((ev, i) => {
                            const pct = toPct(ev.min);
                            const above = i % 2 === 0;
                            return (
                                <Box key={i} sx={{ position: "absolute", left: `${pct}%`, top: "20px", transform: "translateX(-50%)" }}>
                                    <Box sx={{
                                        width: "13px", height: "13px", borderRadius: "50%",
                                        background: ev.hollow ? "white" : ev.color,
                                        border: `2px solid ${ev.color}`,
                                        mx: "auto", position: "relative", zIndex: 1,
                                    }} />
                                    <Typography sx={{
                                        position: "absolute",
                                        ...(above ? { bottom: "16px" } : { top: "16px" }),
                                        left: "50%", transform: "translateX(-50%)",
                                        fontSize: "11px", fontWeight: 700, color: ev.color,
                                        whiteSpace: "nowrap",
                                    }}>
                                        {ev.display}
                                    </Typography>
                                    <Typography sx={{
                                        position: "absolute",
                                        ...(above ? { bottom: "28px" } : { top: "28px" }),
                                        left: "50%", transform: "translateX(-50%)",
                                        fontSize: "10px", color: "#64748b",
                                        whiteSpace: "pre", textAlign: "center", lineHeight: 1.3,
                                    }}>
                                        {ev.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Row 3 — Interventions (points calés sur le même axe horaire) */}
                <Box sx={{ mt: "12px" }}>
                    <Typography sx={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: "8px" }}>
                        Interventions
                    </Typography>
                    <Box sx={{ position: "relative", height: "18px" }}>
                        <Box sx={{ position: "absolute", left: 0, right: 0, top: "8px", height: "2px", background: "#e2e8f0" }} />
                        {(data?.interventions || []).map((iv, i) => {
                            const min = getTimeMin(iv.time);
                            if (min === null) return null;
                            const pct = toPct(min);
                            const impossible = iv.isAccessible === false;
                            return (
                                <Box
                                    key={i}
                                    title={`${getDisplayTime(iv.time) || ""}${iv.nomenclature ? " — " + iv.nomenclature : ""}${impossible ? " (impossible)" : ""}`}
                                    sx={{
                                        position: "absolute", left: `${pct}%`, top: "4px", transform: "translateX(-50%)",
                                        width: "8px", height: "8px", borderRadius: "50%",
                                        background: impossible ? "#ef4444" : "#22c55e",
                                        border: "1px solid white", boxShadow: "0 0 0 0.5px rgba(0,0,0,0.25)",
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>

                {/* Legend + stats */}
                <Box sx={{ display: "flex", gap: "14px", mt: "16px", flexWrap: "wrap", alignItems: "center", borderTop: "1px solid #e5e7eb", pt: "12px" }}>
                    <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        {[
                            { bg: "#bbf7d0", border: "#86efac", label: "Moteur en marche" },
                            { bg: "#fef08a", border: "#fde047", label: "Idle / ralenti" },
                            { bg: "#e2e8f0", border: "#cbd5e1", label: "Moteur arrêté" },
                        ].map((l) => (
                            <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <Box sx={{ width: "18px", height: "10px", borderRadius: "2px", background: l.bg, border: `1px solid ${l.border}` }} />
                                <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{l.label}</Typography>
                            </Box>
                        ))}
                        {[
                            { color: "#374151", hollow: true, label: "Déclaré opérateur" },
                            { color: "#22c55e", hollow: false, label: "Intervention" },
                            { color: "#ef4444", hollow: false, label: "Lavage impossible" },
                        ].map((l) => (
                            <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <Box sx={{ width: "12px", height: "12px", borderRadius: "50%", background: l.hollow ? "white" : l.color, border: `2px solid ${l.color}` }} />
                                <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{l.label}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Metrics */}
                    {reporting && (
                        <Box sx={{ ml: "auto", display: "flex", gap: "16px" }}>
                            {reporting.kmReel != null && (
                                <Box>
                                    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Km réels</Typography>
                                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#3b82f6" }}>{reporting.kmReel} km</Typography>
                                </Box>
                            )}
                            {reporting.fuelConsumption != null && (
                                <Box>
                                    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Emission</Typography>
                                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#ef4444" }}>{reporting.co2Kg ? `${reporting.co2Kg} Kg` : 'N/A'}</Typography>
                                </Box>
                            )}
                            {engineMin !== null && (
                                <Box>
                                    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Temps moteur</Typography>
                                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#374151" }}>{formatMinutes(engineMin)}</Typography>
                                </Box>
                            )}
                            {idleSegments.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Temps idle</Typography>
                                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#854d0e" }}>{formatMinutes(totalIdleMin)}</Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Events chips */}
                {reporting?.events?.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                      {reporting.events.map((evt, i) => (
                        <Chip
                          key={i}
                          icon={evt.type === "ignition_on" ? <EngineOnIcon /> : <EngineOffIcon />}
                          label={evt.time ?? ""}
                          size="medium"
                          variant="outlined"
                        />
                      ))}
                  </Stack>
                )}

            </Box>
        </Box>
    );
}
